import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Wrapper = styled.div`
  position: relative;
  min-height: 320px;

  body.dark & [data-id] {
    filter: invert(1) hue-rotate(180deg);
  }
`;

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 64px 0;
  color: var(--normal-section-color);

  .spinner {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 3px solid var(--footer-outline-focused, rgba(0, 0, 0, 0.1));
    border-top-color: var(--brand-pink);
    animation: ${spin} 0.8s linear infinite;
  }

  .label {
    font-family: Nunito, sans-serif;
    font-size: 14px;
    opacity: 0.7;
  }

  @media (prefers-reduced-motion: reduce) {
    .spinner {
      animation-duration: 2s;
    }
  }
`;

interface Props {
  dataId: string;
}

function TermlyEmbed({ dataId }: Props) {
  const embedRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!document.getElementById("termly-jssdk")) {
      const script = document.createElement("script");
      script.id = "termly-jssdk";
      script.src = "https://app.termly.io/embed-policy.min.js";
      document.body.appendChild(script);
    }

    const target = embedRef.current;
    if (!target) return;

    if (target.childElementCount > 0) {
      setLoaded(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (target.childElementCount > 0) {
        setLoaded(true);
        observer.disconnect();
      }
    });
    observer.observe(target, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [dataId]);

  return (
    <Wrapper>
      {!loaded && (
        <Loading aria-live="polite">
          <div className="spinner" role="presentation" />
          <div className="label">Loading…</div>
        </Loading>
      )}
      <div
        ref={embedRef}
        // @ts-ignore — Termly's script reads the non-standard `name` attribute
        name="termly-embed"
        data-id={dataId}
      />
    </Wrapper>
  );
}

export default TermlyEmbed;
